import { useEffect, useState } from "react";
import { MessageCircle, Mail, Copy, Check, Github, Linkedin, Instagram, Clock } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "./Reveal";
import { WHATSAPP_LINK, EMAIL, PHONE_DISPLAY, SOCIALS } from "@/data/content";

const SOCIAL_ICONS = { Github, Linkedin, Instagram };

const useBrasiliaTime = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const update = () => setTime(fmt.format(new Date()));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
};

export const Contact = () => {
  const [copied, setCopied] = useState(false);
  const time = useBrasiliaTime();

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success("E-mail copiado para a área de transferência!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = EMAIL;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        toast.success("E-mail copiado para a área de transferência!");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Não foi possível copiar. E-mail: " + EMAIL);
      }
    }
  };

  return (
    <section id="contato" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <SectionHeading
        number="08"
        label="Contato"
        title="Vamos tirar seu projeto"
        accent="do papel?"
      />
      <Reveal delay={0.15}>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
          Sem formulários complicados: me chama direto no WhatsApp ou por e-mail.
          Respondo em até 24 horas com uma proposta clara para o seu projeto.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Reveal>
          <a
            data-testid="contact-whatsapp-card"
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="card-glow group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-[#E50914]/40 bg-gradient-to-br from-[#1a0508] to-[#101014] p-8 sm:p-10"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-30 blur-[90px]"
              style={{ background: "#E50914" }}
            />
            <div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#E50914]">
                <MessageCircle className="h-7 w-7 text-white" />
              </div>
              <h3 className="mt-8 font-display text-2xl font-bold text-zinc-100">WhatsApp</h3>
              <p className="mt-2 font-mono-code text-sm text-zinc-400">{PHONE_DISPLAY}</p>
              <p className="mt-4 text-sm leading-relaxed text-zinc-500">
                A forma mais rápida de falar comigo. Clique e a conversa já abre com uma mensagem pronta.
              </p>
            </div>
            <span className="btn-red-glow mt-8 inline-flex w-fit items-center gap-2.5 rounded-full bg-[#E50914] px-7 py-3.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white">
              Chamar agora
            </span>
          </a>
        </Reveal>

        <div className="flex flex-col gap-6">
          <Reveal delay={0.1}>
            <div
              data-testid="contact-email-card"
              className="card-glow flex flex-col rounded-2xl border border-[#22222A] bg-[#101014] p-8 sm:p-10"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#E50914]/30 bg-[#E50914]/10">
                <Mail className="h-7 w-7 text-[#E50914]" />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold text-zinc-100">E-mail</h3>
              <p className="mt-2 break-all font-mono-code text-sm text-zinc-400">{EMAIL}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  data-testid="contact-email-send-btn"
                  href={`mailto:${EMAIL}?subject=Orçamento de Site`}
                  className="btn-red-glow inline-flex items-center gap-2 rounded-full bg-[#E50914] px-6 py-3 font-mono-code text-xs font-bold uppercase tracking-widest text-white"
                >
                  <Mail className="h-4 w-4" />
                  Enviar e-mail
                </a>
                <button
                  data-testid="contact-email-copy-button"
                  onClick={copyEmail}
                  className="inline-flex items-center gap-2 rounded-full border border-[#2A2A33] px-6 py-3 font-mono-code text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#E50914]/60 hover:text-white"
                >
                  {copied ? <Check className="h-4 w-4 text-[#E50914]" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copiado!" : "Copiar"}
                </button>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="card-glow rounded-2xl border border-[#22222A] bg-[#101014] p-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <Clock className="h-4 w-4 text-[#E50914]" />
                  <span className="font-mono-code text-xs uppercase tracking-widest text-zinc-400">
                    Horário de Brasília
                  </span>
                </div>
                <span data-testid="contact-local-time" className="font-mono-code text-lg font-bold tabular-nums text-zinc-100">
                  {time}
                </span>
              </div>
              <div className="mt-6 flex gap-3">
                {SOCIALS.map((social) => {
                  const Icon = SOCIAL_ICONS[social.icon];
                  return (
                    <a
                      key={social.name}
                      data-testid={social.testid}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#2A2A33] text-zinc-400 transition-all duration-300 hover:-translate-y-1 hover:border-[#E50914] hover:text-[#E50914]"
                    >
                      <Icon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
